import { useMutation } from "@apollo/client/react";
import SET_ACCOUNT_PREFERENCE from "@graphql/mutations/SetAccountPreference";

import { useCompanionTooltipsDisabled } from "./use-companion-tooltips-disabled";
import { useCompanionV2 } from "./use-companion-v2";
import { useCurrentUser } from "./use-current-user";
import { getPreferenceValue } from "../utils/preferences";

export const MARK_AS_DONE_TRIGGER_SHOWN_COUNT_KEY =
  "companion-mark-as-done-trigger-shown-count";

const MAX_SHOW_COUNT = 3;

const TOOLTIP_CONTENT =
  "Need help with client communication? I can create a discharge summary, referral letter, or client update.";

const ACTIONS: {
  label: string;
  message: string;
  key: "discharge_summary" | "referral_letter" | "client_update";
}[] = [
  {
    label: "Discharge summary",
    message: "Help me create a discharge summary for this note",
    key: "discharge_summary",
  },
  {
    label: "Referral letter",
    message: "Help me create a referral letter for this note",
    key: "referral_letter",
  },
  {
    label: "Client update",
    message: "Help me create a client update for this note",
    key: "client_update",
  },
];

const parseCount = (preferences: unknown): number => {
  const raw = getPreferenceValue(
    preferences,
    MARK_AS_DONE_TRIGGER_SHOWN_COUNT_KEY,
  );
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

export function useMarkAsDoneTrigger() {
  const { showCompanionTooltip } = useCompanionV2();
  const { user, refetch } = useCurrentUser();
  const tooltipsDisabled = useCompanionTooltipsDisabled();
  const [setAccountPreference] = useMutation(SET_ACCOUNT_PREFERENCE);

  const shownCount = parseCount(user?.account?.preferences);
  const willTriggerMarkAsDone =
    !tooltipsDisabled && shownCount < MAX_SHOW_COUNT;

  const triggerMarkAsDonePrompt = async (
    noteUuid?: string | null,
  ) => {
    if (!willTriggerMarkAsDone) return;
    const nextCount = shownCount + 1;

    await setAccountPreference({
      variables: {
        key: MARK_AS_DONE_TRIGGER_SHOWN_COUNT_KEY,
        value: nextCount,
      },
    });
    await refetch();

    showCompanionTooltip({
      id: "mark-as-done",
      companions: ["quill"],
      content: TOOLTIP_CONTENT,
      actions: ACTIONS.map((a) => ({
        label: a.label,
        message: a.message,
        key: a.key,
      })),
      tracking: {
        trigger: "mark_as_done",
        properties: {
          noteUuid: noteUuid ?? null,
          shownCount: nextCount,
        },
      },
    });
  };

  return { triggerMarkAsDonePrompt, willTriggerMarkAsDone };
}
